import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin, Compass, FileText, Ruler, ShieldCheck, Building2,
  ArrowRight, Phone, MessageCircle, CheckCircle2, Sparkles, HandCoins, LandPlot
} from "lucide-react";
import { api, formatPrice, telLink, whatsappLink, SERVICE_AREAS, PROPERTY_TYPES } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import PropertyCard from "@/components/PropertyCard";
import EnquiryForm from "@/components/EnquiryForm";
import ConsultationModal from "@/components/ConsultationModal";
import SEO, { LOCAL_BUSINESS_JSONLD } from "@/components/SEO";

const HERO = "https://images.unsplash.com/photo-1773470920361-4f6cbb702ce0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsdXh1cnklMjBhcmNoaXRlY3R1cmFsJTIwaG91c2UlMjBpbmRpYXxlbnwwfHx8fDE3OTEwNDMzMzl8MA&ixlib=rb-4.1.0&q=85";

const SERVICE_HIGHLIGHTS = [
  { icon: LandPlot,  title: "Agricultural & Green Land", desc: "Specialists in agricultural, green and plotted land transactions across South 24 Parganas and the Budge Budge belt.", to: "/properties?type=agricultural_land" },
  { icon: FileText,  title: "Govt. Compliance Support", desc: "Mutation, ROR, land conversion and record coordination with Budge Budge II BL&LRO, Alipore SDLRO and district offices.", to: "/documentation" },
  { icon: Compass,   title: "Digital Land Survey", desc: "RTK GNSS, DGPS and Total Station surveys with CAD-ready outputs for green and agricultural plots.", to: "/land-survey" },
  { icon: Building2, title: "Property Buy & Sell", desc: "End-to-end assistance for residential, agricultural and commercial transactions.", to: "/properties" },
];

const WHY_US = [
  { icon: ShieldCheck, title: "Client-focused Consultation", desc: "Honest advice tailored to your requirement — not a one-size-fits-all pitch." },
  { icon: Ruler, title: "Technical Survey Capability", desc: "Modern digital survey workflow for precise boundary and plot information." },
  { icon: CheckCircle2, title: "Transparent Communication", desc: "Clear scope, clear deliverables, and documented process at every step." },
  { icon: MapPin, title: "Local Market Familiarity", desc: "Working knowledge of neighbourhoods across Kolkata and South 24 Parganas." },
  { icon: FileText, title: "Documentation Coordination", desc: "Help organising applications and records — approvals remain with authorities." },
  { icon: Sparkles, title: "Integrated Property Services", desc: "Advisory, surveying and documentation under one accountable brand." },
];

const PROCESS = [
  { step: "01", title: "Initial Consultation", desc: "A quick, no-obligation conversation to understand your property goals." },
  { step: "02", title: "Requirement Assessment", desc: "We clarify scope, timeline, documents and locations with you." },
  { step: "03", title: "Property / Survey Evaluation", desc: "On-ground visits, digital survey where applicable, and shortlist preparation." },
  { step: "04", title: "Documentation Coordination", desc: "Record collection, application support, and liaison tracking." },
  { step: "05", title: "Transaction or Service Completion", desc: "Closure with handover, deliverables and clear next-step guidance." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [search, setSearch] = useState({ intent: "buy", type: "", location: "", budget: "" });
  const [bookingOpen, setBookingOpen] = useState(false);

  useEffect(() => {
    api.get("/properties", { params: { featured: true, limit: 6 } })
      .then((r) => setFeatured(r.data))
      .catch(() => {});
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search.intent) params.set("intent", search.intent);
    if (search.type) params.set("type", search.type);
    if (search.location) params.set("location", search.location);
    if (search.budget) params.set("budget", search.budget);
    window.location.href = `/properties?${params.toString()}`;
  };

  return (
    <>
      <SEO
        title="Real Estate & Land Advisory, Built on Trust"
        description="Apurba Estate — Kolkata-region real estate consultancy for property transactions, digital land survey (RTK GNSS, DGPS, Total Station), and documentation support across South 24 Parganas."
        path="/"
        keywords="real estate consultant Kolkata, land survey South 24 Parganas, RTK GNSS Kolkata, property buy sell Kolkata, land mutation West Bengal, porcha assistance, Bawali, Budge Budge property"
        jsonLd={LOCAL_BUSINESS_JSONLD}
      />
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <img src={HERO} alt="Modern Indian residential architecture" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 ae-hero-overlay" />
        <div className="relative ae-container w-full py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 text-white">
            <div className="ae-eyebrow mb-5">Budge Budge · Alipore · South 24 Parganas</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
              Green Land, Agricultural Plots<br />
              & <span className="text-[#C5A46D]">Government Compliance.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/85 max-w-2xl leading-relaxed">
              Apurba Estate is a Budge Budge-based real estate consultancy focused on agricultural
              and green land, digital land surveying, and government record compliance across the
              Budge Budge II BL&amp;LRO, Alipore SDLRO and district offices of South 24 Parganas.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/properties" className="ae-btn-gold" data-testid="hero-explore-btn">
                Explore Properties <ArrowRight className="w-4 h-4" />
              </Link>
              <button onClick={() => setBookingOpen(true)} className="ae-btn-outline !border-white/30 !text-white hover:!bg-white hover:!text-[#101827]" data-testid="hero-book-btn">
                Book a Consultation
              </button>
              <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="ae-btn-whatsapp" data-testid="hero-whatsapp-btn">
                <MessageCircle className="w-4 h-4" /> WhatsApp Us
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <form onSubmit={onSearch} className="bg-white rounded-2xl shadow-2xl p-5 sm:p-6 border border-white/10" data-testid="hero-search">
              <div className="flex gap-1 bg-[#F4F6F8] p-1 rounded-lg mb-4">
                {["buy", "sell", "rent"].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setSearch({ ...search, intent: v })}
                    className={`flex-1 py-2 rounded-md text-sm font-semibold capitalize transition ${
                      search.intent === v ? "bg-[#101827] text-white" : "text-[#64748B] hover:text-[#101827]"
                    }`}
                    data-testid={`hero-tab-${v}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
              <div className="space-y-3">
                <select value={search.type} onChange={(e) => setSearch({ ...search, type: e.target.value })} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2.5 text-sm text-[#1F2937] bg-white focus:outline-none focus:border-[#183B66]" data-testid="hero-type">
                  <option value="">Property Type (any)</option>
                  {PROPERTY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <input value={search.location} onChange={(e) => setSearch({ ...search, location: e.target.value })} placeholder="Location (e.g. Bawali, New Town)" className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2.5 text-sm" data-testid="hero-location" />
                <input value={search.budget} onChange={(e) => setSearch({ ...search, budget: e.target.value })} placeholder="Budget range (optional)" className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2.5 text-sm" data-testid="hero-budget" />
                <button type="submit" className="ae-btn-primary w-full justify-center" data-testid="hero-search-btn">
                  Search Properties <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-[#F4F6F8] border-y border-[#E2E8F0]">
        <div className="ae-container py-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {SERVICE_HIGHLIGHTS.map((s, i) => (
            <Link key={i} to={s.to} className="group flex items-start gap-3" data-testid={`trust-item-${i}`}>
              <div className="w-11 h-11 shrink-0 rounded-lg bg-white border border-[#E2E8F0] flex items-center justify-center text-[#183B66] group-hover:bg-[#183B66] group-hover:text-white transition">
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-[#101827] text-sm">{s.title}</div>
                <div className="text-xs text-[#64748B] mt-0.5">{s.desc}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="ae-section">
        <div className="ae-container grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="ae-eyebrow mb-3">About Apurba Estate</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#101827] leading-[1.1]">
              More Than Real Estate.<br />
              A Complete Property Advisory Approach.
            </h2>
            <p className="mt-5 text-[#475569] leading-relaxed">
              Apurba Estate provides real estate consultancy and technical land-related services,
              helping clients understand property opportunities, survey requirements, documentation
              processes, and transaction coordination.
            </p>
            <p className="mt-3 text-[#475569] leading-relaxed">
              Our approach combines local property knowledge, technical measurement capability and
              transparent, client-first communication.
            </p>
            <Link to="/about" className="ae-btn-primary mt-7" data-testid="about-cta">
              Discover Apurba Estate <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-6">
            <div className="relative">
              <div className="ae-img-elevated aspect-[4/3]">
                <img src="https://images.unsplash.com/photo-1626249893783-cc4a9f66880a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBsdXh1cnklMjBhcmNoaXRlY3R1cmFsJTIwaG91c2UlMjBpbmRpYXxlbnwwfHx8fDE3OTEwNDMzMzl8MA&ixlib=rb-4.1.0&q=85" alt="Residential architecture" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#101827] text-white rounded-xl p-5 border border-[#C5A46D]/30 shadow-xl max-w-[260px] hidden md:block">
                <div className="ae-eyebrow mb-2">Our Focus</div>
                <div className="text-sm leading-relaxed">
                  Real estate, digital land surveying and documentation — in Kolkata and South 24 Parganas.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="ae-section bg-[#F4F6F8]">
        <div className="ae-container">
          <SectionHeader eyebrow="What We Do" title="Services Built Around Property" subtitle="Advisory, surveying and documentation — delivered with transparency." />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICE_HIGHLIGHTS.map((s, i) => (
              <Link key={i} to={s.to} className="ae-card p-6 flex flex-col" data-testid={`service-card-${i}`}>
                <div className="w-12 h-12 rounded-lg bg-[#101827] text-[#C5A46D] flex items-center justify-center mb-4"><s.icon className="w-5 h-5" /></div>
                <h3 className="font-bold text-[#101827] text-lg mb-1.5">{s.title}</h3>
                <p className="text-[#64748B] text-sm leading-relaxed">{s.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#183B66]">
                  Learn more <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PROPERTIES */}
      <section className="ae-section">
        <div className="ae-container">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <div className="ae-eyebrow mb-2">Featured Listings</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#101827]">Handpicked Properties</h2>
              <p className="text-[#64748B] mt-3 max-w-xl">Representative inventory from our active service area. All demo listings are clearly labelled.</p>
            </div>
            <Link to="/properties" className="ae-btn-outline" data-testid="featured-view-all">
              View all properties <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p) => <PropertyCard key={p.id} p={p} />)}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="ae-section bg-[#101827] text-white">
        <div className="ae-container">
          <div className="max-w-3xl">
            <div className="ae-eyebrow mb-3">Why Choose Apurba Estate</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.1]">
              Service principles we quietly live by.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {WHY_US.map((w, i) => (
              <div key={i} className="rounded-xl p-6 border border-white/10 bg-[#162035] hover:border-[#C5A46D]/50 transition" data-testid={`why-us-${i}`}>
                <div className="w-11 h-11 rounded-lg bg-[#C5A46D]/15 text-[#C5A46D] flex items-center justify-center mb-4"><w.icon className="w-5 h-5" /></div>
                <h3 className="font-semibold text-white text-lg mb-1.5">{w.title}</h3>
                <p className="text-white/65 text-sm leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="ae-section">
        <div className="ae-container">
          <SectionHeader eyebrow="How We Work" title="A clear, five-step process" subtitle="Simple, documented and transparent — the way property matters should be." />
          <div className="grid md:grid-cols-5 gap-5">
            {PROCESS.map((p, i) => (
              <div key={p.step} className="relative" data-testid={`process-${i}`}>
                <div className="text-5xl font-display font-semibold text-[#C5A46D]/25 mb-2">{p.step}</div>
                <h3 className="font-semibold text-[#101827] text-lg mb-1.5">{p.title}</h3>
                <p className="text-[#64748B] text-sm">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMING SOON — Finance Compliance */}
      <section className="ae-section pt-0">
        <div className="ae-container">
          <div className="relative overflow-hidden rounded-2xl border border-[#C5A46D]/30 bg-gradient-to-br from-[#F4F6F8] to-white p-8 md:p-12 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 ae-chip mb-4">
                <Sparkles className="w-3.5 h-3.5" /> Coming Soon
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-[#101827] leading-tight">
                Tax, GST & Finance Compliance Consulting
              </h2>
              <p className="mt-4 text-[#475569] leading-relaxed max-w-2xl">
                We are expanding Apurba Estate's advisory practice to include tax, GST, and finance
                compliance consulting — purpose-built for landowners, farmers, agri-businesses and
                property investors across South 24 Parganas. Sign up to be notified when this launches.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/contact" className="ae-btn-primary">Register your interest <ArrowRight className="w-4 h-4" /></Link>
                <a href={whatsappLink("Hello Apurba Estate, I'd like to be notified when the Tax / GST / Finance compliance consulting launches.")} target="_blank" rel="noopener noreferrer" className="ae-btn-whatsapp">
                  <MessageCircle className="w-4 h-4" /> Notify me on WhatsApp
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-3">
                {["Income Tax Advisory", "GST Registration & Filing", "Agri-Business Compliance", "Land Investment Tax Planning"].map((p) => (
                  <div key={p} className="ae-card p-4 text-sm font-semibold text-[#101827] flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#C5A46D] shrink-0 mt-0.5" /> {p}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AREAS */}
      <section className="ae-section bg-[#F4F6F8]">
        <div className="ae-container">
          <SectionHeader eyebrow="Areas We Serve" title="Deeply local across Kolkata & South 24 Parganas" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {SERVICE_AREAS.map((a, i) => (
              <div key={a} className="ae-card p-4 flex items-center gap-2 text-sm font-semibold text-[#101827]" data-testid={`area-${i}`}>
                <MapPin className="w-4 h-4 text-[#C5A46D]" /> {a}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="ae-section">
        <div className="ae-container">
          <div className="relative overflow-hidden rounded-2xl bg-[#101827] text-white p-8 md:p-14 grid lg:grid-cols-12 gap-10 items-center">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#C5A46D]/10 blur-3xl" />
            <div className="lg:col-span-7 relative">
              <div className="ae-eyebrow mb-3">Ready When You Are</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.1]">
                Have a property requirement?<br />
                <span className="text-[#C5A46D]">Let's discuss your next move.</span>
              </h2>
              <div className="mt-7 flex flex-wrap gap-3">
                <button onClick={() => setBookingOpen(true)} className="ae-btn-gold" data-testid="final-book-btn">Book Consultation</button>
                <Link to="/sell" className="ae-btn-outline !border-white/30 !text-white hover:!bg-white hover:!text-[#101827]" data-testid="final-list-btn">List Your Property</Link>
                <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="ae-btn-whatsapp"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="bg-white text-[#101827] rounded-xl p-5">
                <EnquiryForm compact defaultService="general_enquiry" sourcePage="home_cta" title="Quick enquiry" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <ConsultationModal open={bookingOpen} onClose={() => setBookingOpen(false)} />
    </>
  );
}
